/**
 * Troubleshooting Tracking Types
 *
 * This module defines types for the extensible troubleshooting system
 * that tracks UI activities for debugging purposes.
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
}

export enum DataProviderTriggerType {
  INITIAL_LOAD = 'INITIAL_LOAD',
  PARENT_CHANGE = 'PARENT_CHANGE',
  PAGINATION = 'PAGINATION',
}

export enum DataProviderType {
  HTTP = 'HTTP',
  SQL = 'SQL',
}

export enum TrackingEventType {
  // Legacy cascading events (deprecated, will be removed)
  CASCADING_VALUE_SELECTED = 'CASCADING_VALUE_SELECTED',
  CASCADING_API_TRIGGERED = 'CASCADING_API_TRIGGERED',
  CASCADING_API_SUCCESS = 'CASCADING_API_SUCCESS',
  CASCADING_API_ERROR = 'CASCADING_API_ERROR',

  // New data provider events (all API calls)
  DATA_PROVIDER_VALUE_SELECTED = 'DATA_PROVIDER_VALUE_SELECTED',
  DATA_PROVIDER_API_TRIGGERED = 'DATA_PROVIDER_API_TRIGGERED',
  DATA_PROVIDER_API_SUCCESS = 'DATA_PROVIDER_API_SUCCESS',
  DATA_PROVIDER_API_ERROR = 'DATA_PROVIDER_API_ERROR',

  // Form API events (form/get and form/da calls)
  FORM_API_GET_SUCCESS = 'FORM_API_GET_SUCCESS',
  FORM_API_GET_ERROR = 'FORM_API_GET_ERROR',
  FORM_API_DA_SUCCESS = 'FORM_API_DA_SUCCESS',
  FORM_API_DA_ERROR = 'FORM_API_DA_ERROR',

  // Form render events
  FORM_RENDER_END = 'FORM_RENDER_END',
}

export enum TrackingFeature {
  CASCADING = 'CASCADING', // Legacy, will be removed
  DATA_PROVIDER = 'DATA_PROVIDER', // Single feature for all data provider tracking
  FORM_API = 'FORM_API', // Form get/DA API calls
  FORM_RENDER = 'FORM_RENDER', // Form rendering performance
}

export interface TrackingEvent {
  id: string;
  correlationId?: string; // Links related events (e.g., value selection → API call)
  parentEventId?: string; // Links to triggering event
  timestamp: number;
  feature: TrackingFeature;
  type: TrackingEventType;
  data: Record<string, unknown>;
  duration?: number; // Duration in milliseconds
  level?: LogLevel; // Default: INFO
  metadata?: Record<string, unknown>;
  // Data provider specific fields at first level for easy access
  triggerType?: DataProviderTriggerType;
  dataProviderType?: DataProviderType;
}

export interface CascadingTrackingData {
  fieldId: string;
  fieldName: string;
  selectedValue?: unknown;
  dependencies?: Record<string, unknown>;
  apiUrl?: string;
  apiPayload?: unknown;
  apiResponse?: unknown;
  error?: string;
  triggerReason?: string;
}

// Partial data for API success/error tracking (when not all fields are available)
export interface CascadingTrackingDataPartial {
  fieldId: string;
  fieldName: string;
  apiUrl?: string;
  apiResponse?: unknown;
  error?: string;
}

export interface TrackingOptions {
  maxEvents?: number; // Maximum number of events to store (default: 200)
  enabled?: boolean; // Whether tracking is enabled (default: true in dev)
  logLevel?: LogLevel; // Minimum log level to output (default: INFO)
}

export interface TrackingFilter {
  feature?: TrackingFeature;
  type?: TrackingEventType;
  startTime?: number;
  endTime?: number;
  fieldId?: string;
  level?: LogLevel;
}

export interface DataProviderTrackingData {
  // Field information
  fieldId: string;
  fieldName: string;
  fieldState?: {
    isReadOnly?: boolean;
    isDisabled?: boolean;
    isRequired?: boolean;
  };

  // Trigger information
  triggerType: DataProviderTriggerType;
  dataProviderType: DataProviderType;

  // Parent dependency information
  parentFields?: Array<{
    fieldId: string;
    fieldName: string;
    selectedValue: unknown;
  }>;

  // API information
  apiUrl?: string; // For HTTP
  sqlIdentifier?: string; // For SQL
  httpMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE'; // For HTTP
  page?: number; // Page number for pagination
  pageSize?: number;

  // Timing (will be set by tracker)
  startTime: number;
  endTime?: number;
  duration?: number;

  // Response metadata (no actual data stored)
  apiResponse?: {
    statusCode?: number; // HTTP status code
    dataCount?: number; // Number of items returned
    hasNext?: boolean; // Pagination info
    totalPages?: number; // If available
  };

  // Error information
  error?: {
    type: 'network' | 'api' | 'timeout' | 'cancelled' | 'unknown';
    statusCode?: number; // HTTP status code if applicable
    message: string;
  };

  // Status
  status: 'success' | 'error' | 'pending' | 'cancelled';

  // Context information
  context?: {
    urlSlug?: string;
    entityId?: string;
    formId?: string;
  };

  // Request tracking
  isConcurrent?: boolean; // Multiple requests happening simultaneously
  wasCancelled?: boolean; // Request was cancelled

  // Metadata for additional info
  metadata?: Record<string, unknown>; // Allow other metadata
}

// Partial data for API success/error tracking (when not all fields are available)
export interface DataProviderTrackingDataPartial {
  fieldId: string;
  fieldName: string;
  triggerType?: DataProviderTriggerType; // Include triggerType in success/error events
  apiUrl?: string;
  apiResponse?: {
    statusCode?: number;
    dataCount?: number;
    hasNext?: boolean;
    totalPages?: number;
  };
  error?: {
    type: 'network' | 'api' | 'timeout' | 'cancelled' | 'unknown';
    statusCode?: number;
    message: string;
  };
}

export interface Statistics {
  totalEvents: number;
  eventsByFeature: Record<string, number>;
  eventsByType: Record<string, number>;
  averageDuration?: number;
  oldestEvent?: number;
  newestEvent?: number;

  // New data provider specific stats
  dataProviderStats?: {
    // Overall metrics
    totalApiCalls: number;
    successCount: number;
    errorCount: number;
    successRate: number; // Percentage
    errorRate: number; // Percentage

    // Performance metrics
    averageResponseTime: number;
    medianResponseTime: number;
    minResponseTime: number;
    maxResponseTime: number;
    slowestCalls: Array<{
      fieldId: string;
      fieldName: string;
      duration: number;
      triggerType: DataProviderTriggerType;
      timestamp: number;
      statusCode?: number;
    }>;

    // Breakdown metrics
    callsByElementId: Record<
      string,
      {
        total: number;
        success: number;
        error: number;
      }
    >;
    callsByTriggerType: Record<
      DataProviderTriggerType,
      {
        total: number;
        success: number;
        error: number;
        errorRate: number;
      }
    >;
    callsByProviderType: Record<
      DataProviderType,
      {
        total: number;
        success: number;
        error: number;
        errorRate: number;
      }
    >;
    callsByStatusCode: Record<number, number>; // HTTP status codes

    // Response metrics
    averageResponseDataCount: number;
    totalItemsLoaded: number;
    averagePageNumber: number;

    // Error breakdown
    errorsByType: Record<string, number>; // network, api, timeout, etc.
    errorsByStatusCode: Record<number, number>; // HTTP error codes
  };

  // Performance metrics for MFE, Form API, and Render
  performanceStats?: {
    // MFE lifecycle timing (from Performance API marks)
    mfeBootstrapTime?: number;
    mfeMountTime?: number;
    mfeTotalLoadTime?: number;

    // Form API timing
    formApiCalls: Array<{
      type: 'GET' | 'DA';
      formId: string;
      duration: number;
      status: 'success' | 'error';
      timestamp: number;
    }>;

    // Form render timing
    formRenderCalls: Array<{
      fieldCount: number;
      duration: number;
      urlSlug?: string;
      timestamp: number;
    }>;

    // Initial load timing (aggregated from DATA_PROVIDER events)
    initLoadTime?: number;
    initLoadApiCount?: number;
    initLoadSuccessCount?: number;
    initLoadErrorCount?: number;
  };
}

/**
 * Dependency map structure for tracking field relationships
 */
export interface DependencyMap {
  // Map of fieldId -> array of parent fieldIds that this field depends on
  dependencies: Record<string, Set<string>>;
  // Map of fieldId -> array of child fieldIds that depend on this field
  dependents: Record<string, Set<string>>;
  // Map of fieldId -> fieldName for display purposes
  fieldNames: Record<string, string>;
}

/**
 * Tracks the source of dependency information
 */
export interface DependencySource {
  source: 'saved' | 'extracted' | 'both';
  savedDependencies?: Record<string, string[]>;
  extractedDependencies?: Record<string, string[]>;
}

/**
 * Form structure for building dependency map
 */
export interface FormStructureForDependencyMap {
  canvasFields: Array<{
    id: string;
    fieldName?: string;
    dataProvider?: string; // For compatibility
    dataProviderId?: string; // Actual field property
    expressions?: Array<{
      type: string;
      value: Array<{ kind: string; value: string; selected: boolean }>;
      enabled: boolean;
    }>;
    defaultValue?: { value: string; isExpression: boolean };
  }>;
  dataProviders: Record<
    string,
    {
      dependencies?: Record<string, string[]>;
      dependencySource?: DependencySource; // Track source of dependencies
    }
  >;
}

/**
 * Dependency relationship information
 */
export interface DependencyRelationship {
  fieldId: string;
  fieldName: string;
  dependsOn: Array<{
    fieldId: string;
    fieldName: string;
  }>;
  dependents: Array<{
    fieldId: string;
    fieldName: string;
  }>;
}

/**
 * Hierarchy tree node structure
 */
export interface HierarchyTreeNode {
  fieldId: string;
  fieldName: string;
  children: HierarchyTreeNode[];
  dependencies: string[]; // All dependencies (including cross-dependencies)
  level: number;
  isReference?: boolean; // True if this node appears under multiple parents (shown as reference)
}

/**
 * Level-wise hierarchy structure
 */
export interface LevelWiseHierarchy {
  levels: Record<
    number,
    Array<{
      fieldId: string;
      fieldName: string;
      dependencies: string[];
    }>
  >;
  maxLevel: number;
}
